import { useEffect, useState } from 'react';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import { createCampaign, updateCampaign } from '@app/services/campaigns';
import {
  Button,
  CustomDatePicker,
  HookedForm,
  InputField,
  SelectField,
  DesignPreviewDialog,
  DesignTemplatePicker,
  useDesignTemplateFields,
} from '@app/components';
import { SwitchField } from '@app/components/Fields/SwitchField';
import { useNavigate } from 'react-router-dom';
import { formatDateToISO } from '@app/utils/utils';
import { toast } from 'react-toastify';
import AddIcon from '@app/assets/icons/add.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { resetForm, updateFormData } from '@app/redux/features/campaignForm';
import dayjs from 'dayjs';
import { TemplateList } from '@app/components/TemplateList';
import { useFormChangeDetection } from './useFormChangeDetection';
import DraftIcon from '@app/assets/icons/draft.svg?react';
import eyeIcon from '@app/assets/icons/eye.png';
import { MessageTemplateChannel } from '@app/utils/consts';

const FORM_STORAGE_KEY = 'campaignFormData';

export const CreateCampaignCampaign = ({
  form,
}: {
  form: UseFormReturn<FieldValues, unknown, undefined>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getValues, handleSubmit } = form;
  const [followUpRows, setFollowUpRows] = useState([0]);
  const formData = useAppSelector((state) => state.campaignForm.formData);
  const [sendImmediatelyValue, setSendImmediatelyValue] = useState(false);

  // Watch sendImmediately value
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'sendImmediately') {
        setSendImmediatelyValue(!!value.sendImmediately);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Use the custom hook for form change detection
  const { hasUnsavedChanges, resetUnsavedChanges } =
    useFormChangeDetection(form);

  // `null` template id = business default. A custom (edited) design is sent as HTML, as before, and wins over the linked template
  const selectedPlatforms = useWatch({ control: form.control, name: 'platforms' }) as string[] | undefined;
  const {
    emailTemplateId,
    smsTemplateId,
    customEmailDesign,
    customMessageDesign,
    emailDesign,
    smsDesign,
    setTemplateId,
    resetCustomDesign,
    editingDesign,
    startEditing,
    closeDesignEditor,
    handleTemplateLinkError,
    previewChannel,
    setPreviewChannel,
  } = useDesignTemplateFields(form, FORM_STORAGE_KEY);
  // Without a platform selection both pickers are shown, as before
  const showEmailTemplate = !selectedPlatforms?.length || selectedPlatforms.includes('email');
  const showSmsTemplate = !selectedPlatforms?.length || selectedPlatforms.includes('sms');

  // Override the navigate function to check for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Initialize form with saved data for this step
  useEffect(() => {
    // First check localStorage for saved form data
    const storedFormData = localStorage.getItem('campaignFormData');
    if (storedFormData) {
      const parsedData = JSON.parse(storedFormData);
      Object.keys(parsedData).forEach((key) => {
        if (parsedData[key] !== undefined) {
          form.setValue(key, parsedData[key]);
        }
      });

      // If follow-up rows were previously set, restore them
      const savedFollowUpDays1 = parsedData.followUpDays1;
      const savedFollowUpDays2 = parsedData.followUpDays2;

      if (savedFollowUpDays1) {
        setFollowUpRows([0, 1]);
      }
      if (savedFollowUpDays2) {
        setFollowUpRows([0, 1, 2]);
      }
    }
    // Then check Redux state if localStorage didn't have data
    else if (formData) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined) {
          form.setValue(key, formData[key]);
        }
      });

      // If follow-up rows were previously set, restore them
      const savedFollowUpDays1 = formData.followUpDays1;
      const savedFollowUpDays2 = formData.followUpDays2;

      if (savedFollowUpDays1) {
        setFollowUpRows([0, 1]);
      }
      if (savedFollowUpDays2) {
        setFollowUpRows([0, 1, 2]);
      }
    }

  }, [form, formData]);

  const addFollowUpRow = () => {
    if (followUpRows.length < 3) {
      setFollowUpRows([...followUpRows, followUpRows.length]);
    }
  };

  const removeFollowUpRow = (index: number) => {
    setFollowUpRows(followUpRows.filter((_, i) => i !== index));

    // Clear the form values for the removed row
    const rowNumber = followUpRows[index];
    form.setValue(`followUpDays${rowNumber}`, undefined);
    form.setValue(`followUpPeriod${rowNumber}`, undefined);

    // Save the updated form data
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
  };

  const handleBack = () => {
    // Save current form data before navigating
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
    navigate('/campaigns/create/customers');
  };

  const validateFollowUpFields = (formData: FieldValues) => {
    const missingFollowUpFields =
      formData['sendFollow'] &&
      followUpRows.some((rowIndex) => {
        const days = formData[`followUpDays${rowIndex}`];
        const period = formData[`followUpPeriod${rowIndex}`];
        return (
          days === undefined ||
          days === '' ||
          period === undefined ||
          period === ''
        );
      });
    if (missingFollowUpFields) {
      toast.error(
        'Please provide both the number of days and the period for each follow-up.'
      );
      return false;
    }
    const invalidFollowUpDays = followUpRows.some((rowIndex) => {
      const days = formData[`followUpDays${rowIndex}`];
      return days > 3; // 3 days = 72 hours
    });
    if (invalidFollowUpDays) {
      toast.error('Follow-up days cannot exceed 72 hours (3 days).');
      return false;
    }
    return true;
  };
  const onSubmit = async (statusOverride?: string) => {
    // Check if we're editing an existing campaign
    const formData = getValues();
    const isEditing = formData.campaignId;

    if (formData.campaignName === '' || !formData.campaignName) {
      toast.error('Campaign Settings is required.');
      navigate('/campaigns/create/settings');
      return;
    }

    try {
      setIsSubmitting(true);

      const validFollowup = validateFollowUpFields(formData);
      if (!validFollowup) {
        setIsSubmitting(false);
        return;
      }

      // Save one last time before submission
      // Merge current form values with any previously saved template fields
      const existingRaw = localStorage.getItem('campaignFormData');
      const existingStored = existingRaw ? JSON.parse(existingRaw) : {};
      const mergedFormData = { ...existingStored, ...formData };
      dispatch(updateFormData(mergedFormData));
      localStorage.setItem('campaignFormData', JSON.stringify(mergedFormData));
      // Only process follow-up data for active rows with values
      const followUpNumbers = followUpRows
        .map((rowIndex) => formData[`followUpDays${rowIndex}`])
        .filter(
          (value) => value !== undefined && value !== '' && value !== null
        )
        .map((value) => value.toString());
      const followUpPeriods = followUpRows
        .map((rowIndex) => formData[`followUpPeriod${rowIndex}`])
        .filter(
          (value) => value !== undefined && value !== '' && value !== null
        )
        .map((value) => value.toString());

      // Get the updated email template from EmailDesignEditor if available
      const storedFormData = localStorage.getItem('campaignFormData');
      const parsedStoredData = storedFormData ? JSON.parse(storedFormData) : {};

      // HTML/text only for an edited (custom) channel; empty lets the backend render the linked template or the business default
      const updatedEmailTemplate = parsedStoredData.emailDesignContent ? parsedStoredData.updatedEmailTemplate || '' : '';
      const updatedMessageTemplate = parsedStoredData.messageDesignContent ? parsedStoredData.updatedMessageTemplate || '' : '';
      const updatedReviewFormTemplate =
        parsedStoredData.updatedReviewFormTemplate || '';
      const updatedFeedbackFormTemplate =
        parsedStoredData.updatedFeedbackFormTemplate || '';
      const updatedPositiveWindowTemplate =
        parsedStoredData.updatedPositiveWindowTemplate || '';
      const updatedNegativeWindowTemplate =
        parsedStoredData.updatedNegativeWindowTemplate || '';
      const emailDesignContent = parsedStoredData.emailDesignContent || {};
      const reviewFormDesignContent =
        parsedStoredData.reviewFormDesignContent || {};
      const feedbackFormDesignContent =
        parsedStoredData.feedbackFormDesignContent || {};
      const positiveWindowDesignContent =
        parsedStoredData.positiveWindowDesignContent || {};
      const negativeWindowDesignContent =
        parsedStoredData.negativeWindowDesignContent || {};

      const campaignData = {
        name: formData.campaignName,
        description: formData.description,
        type: formData.type || 'standard',
        customers: formData.selectedCustomers || [],
        sendImmediately: formData.sendImmediately,
        startDate: formData.initialSendDate,
        schedule: {
          startDate: formData.initialSendDate
            ? dayjs(formData.initialSendDate).format('MM/DD/YYYY')
            : dayjs().format('MM/DD/YYYY'),
          time: formData.initialSendDate
            ? dayjs(formData.initialSendDate).format('HH:mm:ss')
            : undefined,
        },
        template: {
          subject: emailDesignContent.subject || 'How was your experience?',
          emailTemplate: encodeURIComponent(updatedEmailTemplate) || '', // Email design HTML template
          textTemplate: encodeURIComponent(updatedMessageTemplate) || '', // Only message design HTML template
          reviewFormTemplate:
            encodeURIComponent(updatedReviewFormTemplate) || '', // Review form design HTML template
          feedbackFormTemplate:
            encodeURIComponent(updatedFeedbackFormTemplate) || '', // Feedback form design HTML template
          positiveWindowTemplate:
            encodeURIComponent(updatedPositiveWindowTemplate) || '', // Positive window design HTML template
          negativeWindowTemplate:
            encodeURIComponent(updatedNegativeWindowTemplate) || '', // Negative window design HTML template
        },
        platforms: formData.platforms,
        // Sent on every save: an omitted id keeps the old link on update, `null` = business default
        emailTemplateId: formData.emailTemplateId ?? null,
        smsTemplateId: formData.smsTemplateId ?? null,
        sendFollowUp: formData.sendFollowUp,
        followUpRows: followUpNumbers,
        followUpPeriods: followUpPeriods,
        status: statusOverride || 'ACTIVE',
        senderName: formData.businessName,
        senderAddress: formData.businessAddress,
        senderEmail: formData.businessEmail,
        reviewFormDesign: reviewFormDesignContent, // Review form design configuration
        feedbackFormDesign: feedbackFormDesignContent, // Feedback form design configuration
        positiveWindowDesign: positiveWindowDesignContent, // Positive window design configuration
        negativeWindowDesign: negativeWindowDesignContent, // Negative window design configuration
      };

      if (isEditing) {
        // Update existing campaign
        await updateCampaign(formData.campaignId, campaignData);
        toast.success('Campaign updated successfully');
      } else {
        // Create new campaign
        const response = await createCampaign(campaignData);
        const newCampaignId =
          response.data?.campaign?.id ||
          response.data?.data?.id ||
          response.data?.id;
        toast.success('Campaign created successfully');

        // Clear form state
        form.reset();

        // Reset form data in Redux
        dispatch(resetForm());

        // Clear localStorage completely
        localStorage.removeItem('campaignFormData');
        localStorage.removeItem('campaignSelectedLists');

        // Reset unsaved changes flag
        resetUnsavedChanges();

        // Navigate back to campaigns list with highlight
        if (newCampaignId) {
          navigate(`/campaigns?highlight=${newCampaignId}`);
        } else {
          navigate('/campaigns');
        }
        return;
      }

      // Clear form state
      form.reset();

      // Reset form data in Redux
      dispatch(resetForm());

      // Clear localStorage completely
      localStorage.removeItem('campaignFormData');
      localStorage.removeItem('campaignSelectedLists');

      // Reset unsaved changes flag
      resetUnsavedChanges();

      // Navigate back to campaigns list
      navigate('/campaigns');
    } catch (error: any) {
      console.error('Error creating/updating campaign:', error);
      // Handled here: a deleted linked template resets the channel; plan limits are already shown by the api interceptor
      if (handleTemplateLinkError(error) || error?.data?.name === 'plan_exeeded') return;
      if (error?.data?.message)
        toast.error(
          error?.data?.message,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
      else if (error?.data?.error?.message)
        toast.error(
          isEditing ? error?.data?.error?.message : error?.data?.error?.message,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
      else
        toast.error(
          isEditing ? 'Failed to update campaign' : 'Failed to create campaign',
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Watch for changes in the sendImmediately field
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'sendImmediately') {
        if (value.sendImmediately) {
          const formattedDate = formatDateToISO(new Date());
          form.setValue('initialSendDate', formattedDate);
        }
      }
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, [form]);

  if (editingDesign) {
    return (
      <div className="w-full h-screen bg-white overflow-hidden">
        <TemplateList
          open
          onClose={closeDesignEditor}
          // Only the legacy HTML editor inside TemplateList calls this; the HTML is read from storage on submit
          onSelectTemplate={() => {}}
          templateMode={editingDesign.channel === MessageTemplateChannel.EMAIL ? 'email' : 'text'}
          storageProperty={FORM_STORAGE_KEY}
          initialEditingType={editingDesign.channel === MessageTemplateChannel.EMAIL ? 'email' : 'message'}
          initialEmailContent={editingDesign.channel === MessageTemplateChannel.EMAIL ? editingDesign.content : undefined}
          initialMessageContent={editingDesign.channel === MessageTemplateChannel.SMS ? editingDesign.content : undefined}
        />
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto mb-24 p-4 flex flex-col justify-between items-center gap-4">
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Initial sending</h4>
            <p className="text-xs text-base-coolGray">
              Set a date for your first send of this campaign
            </p>
          </div>
          <hr />
          <HookedForm formHook={form} className="space-y-4">
            <SwitchField
              name="sendImmediately"
              label={<span>Send immediately</span>}
            />
            <CustomDatePicker
              name="initialSendDate"
              label="When should we send the initial campaign? "
              preventPastDates={true}
              position="right"
              sendImmediately={sendImmediatelyValue}
            />
          </HookedForm>
        </div>
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Design templates</h4>
            <p className="text-xs text-base-coolGray">
              Choose a design template for windows or create a new one
            </p>
          </div>
          <hr />
          <div className="space-y-4">
            {showEmailTemplate && (
              <DesignTemplatePicker
                channel={MessageTemplateChannel.EMAIL}
                label="Email template"
                value={emailTemplateId}
                onChange={(templateId) => setTemplateId(MessageTemplateChannel.EMAIL, templateId)}
                customContent={customEmailDesign}
                onEdit={(content) => startEditing({ channel: MessageTemplateChannel.EMAIL, content })}
                onReset={() => resetCustomDesign(MessageTemplateChannel.EMAIL)}
              />
            )}
            {showSmsTemplate && (
              <DesignTemplatePicker
                channel={MessageTemplateChannel.SMS}
                label="Text template"
                value={smsTemplateId}
                onChange={(templateId) => setTemplateId(MessageTemplateChannel.SMS, templateId)}
                customContent={customMessageDesign}
                onEdit={(content) => startEditing({ channel: MessageTemplateChannel.SMS, content })}
                onReset={() => resetCustomDesign(MessageTemplateChannel.SMS)}
              />
            )}
          </div>
        </div>
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Follow-ups</h4>
            <p className="text-xs text-base-coolGray">
              You can also send follow-up requests after a while to customers
              who have not left a review.
            </p>
          </div>
          <hr />
          <HookedForm formHook={form} className="space-y-4">
            <SwitchField
              name="sendFollowUp"
              label={<span>Send follow-up</span>}
            />
            <p className="text-sm">Send follow-up</p>

            {followUpRows.map((rowIndex, index) => (
              <div key={rowIndex} className="flex gap-4 items-center">
                <InputField
                  name={`when${index}`}
                  value="After"
                  disabled
                  className="!w-[100px] opacity-90 shrink-0"
                  containerClass="!w-fit"
                />
                <input
                  type="number"
                  className="!w-16 bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1"
                  min="1"
                  max="3"
                  {...form.register(`followUpDays${rowIndex}`)}
                  onChange={(e) => {
                    form.register(`followUpDays${rowIndex}`).onChange(e);
                    // Save form data immediately
                    const values = form.getValues();
                    dispatch(updateFormData(values));
                    localStorage.setItem(
                      'campaignFormData',
                      JSON.stringify(values)
                    );
                  }}
                />
                <SelectField
                  options={[{ label: 'Day(s)', value: 'days' }]}
                  name={`followUpPeriod${rowIndex}`}
                  defaultValue={'days'}
                />

                <div className="flex gap-2 ml-2">
                  <button
                    type="button"
                    onClick={() => setPreviewChannel(MessageTemplateChannel.EMAIL)}
                    className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px] hover:bg-[#D1D5DB] transition-colors"
                  >
                    <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewChannel(MessageTemplateChannel.SMS)}
                    className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px] hover:bg-[#D1D5DB] transition-colors"
                  >
                    <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2" />
                    Text
                  </button>
                </div>

                {index === followUpRows.length - 1 &&
                  followUpRows.length < 3 && (
                    <Button
                      kind="secondary"
                      type="button"
                      className="!min-w-fit"
                      onClick={addFollowUpRow}
                    >
                      <AddIcon className="h-6 w-6" />
                    </Button>
                  )}

                {index > 0 && (
                  <Button
                    kind="secondary"
                    className="!min-w-fit"
                    type="button"
                    onClick={() => removeFollowUpRow(index)}
                  >
                    <CloseIcon className="h-6 w-6" />
                  </Button>
                )}
              </div>
            ))}

            {followUpRows.length === 3 && (
              <p className="text-xs text-gray-500">
                Maximum of 3 follow-ups allowed
              </p>
            )}
          </HookedForm>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 p-4 w-full sticky bottom-0">
        <div className="w-full px-4 flex justify-between items-center">
          <Button
            kind="secondary"
            type="button"
            onClick={handleBack}
            className="bg-secondary border min-w-fit ml-8"
          >
            <ArrowLeft />
          </Button>
          <div className="flex gap-2">
            <Button
              kind="secondary"
              type="button"
              onClick={() => handleSubmit(() => onSubmit('DRAFT'))()}
              disabled={isSubmitting}
              leftNode={<DraftIcon />}
            >
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={handleSubmit(() => onSubmit())}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </div>
      </div>

      {/* Follow-ups are sent with the same designs as the first message */}
      <DesignPreviewDialog
        open={previewChannel !== null}
        onClose={() => setPreviewChannel(null)}
        title={previewChannel === MessageTemplateChannel.SMS ? smsDesign.title : emailDesign.title}
        channel={previewChannel ?? MessageTemplateChannel.EMAIL}
        content={previewChannel === MessageTemplateChannel.SMS ? smsDesign.content : emailDesign.content}
      />
    </>
  );
};
